<script setup>
import { ref } from 'vue';
import { useStore } from 'vuex';

import ReportHeader from './components/ReportHeader.vue';
import ReportFilterSelector from './components/FilterSelector.vue';
import ReportMetricCard from './components/ReportMetricCard.vue';
import ClassificationRankingTable from './components/tables/ClassificationRankingTable.vue';
import V4Button from 'dashboard/components-next/button/Button.vue';
import ReportsAPI from 'dashboard/api/reports';
import { generateFileName } from 'dashboard/helper/downloadHelper';

const store = useStore();

// Filter state
const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const inboxIds = ref([]);

// Data state
const metrics = ref({
  total_conversations: 0,
  classified_count: 0,
  classified_percentage: 0,
  unclassified_count: 0,
  unclassified_percentage: 0,
});
const rankings = ref([]);
const pagination = ref({
  current_page: 1,
  total_pages: 1,
  total_count: 0,
  per_page: 25,
});
const isLoading = ref(false);

const fetchData = async (page = 1) => {
  if (!from.value || !to.value) return;

  isLoading.value = true;
  try {
    const response = await ReportsAPI.getClassificationReports({
      from: from.value,
      to: to.value,
      inboxIds: inboxIds.value,
      businessHours: businessHours.value,
      page,
    });
    metrics.value = response.data.metrics;
    rankings.value = response.data.classifications;
    pagination.value = response.data.meta;
  } catch (error) {
    metrics.value = {
      total_conversations: 0,
      classified_count: 0,
      classified_percentage: 0,
      unclassified_count: 0,
      unclassified_percentage: 0,
    };
    rankings.value = [];
  } finally {
    isLoading.value = false;
  }
};

const onFilterChange = ({
  from: fromDate,
  to: toDate,
  businessHours: bh,
  selectedInboxes,
}) => {
  from.value = fromDate;
  to.value = toDate;
  businessHours.value = bh;
  inboxIds.value = selectedInboxes?.map(el => el.id) || [];
  fetchData(1);
};

const onPageChange = page => {
  fetchData(page);
};

const onDownloadClick = () => {
  const fileName = generateFileName({ type: 'classifications', to: to.value });
  store.dispatch('downloadClassificationReports', {
    from: from.value,
    to: to.value,
    inboxIds: inboxIds.value,
    businessHours: businessHours.value,
    fileName,
  });
};

const formatNumber = value => {
  return new Intl.NumberFormat().format(value);
};

const formatPercentage = (count, percentage) => {
  return `${formatNumber(count)} (${percentage}%)`;
};
</script>

<template>
  <ReportHeader :header-title="$t('CLASSIFICATION_REPORTS.HEADER')">
    <V4Button
      :label="$t('CLASSIFICATION_REPORTS.DOWNLOAD')"
      icon="i-ph-download-simple"
      size="sm"
      @click="onDownloadClick"
    />
  </ReportHeader>
  <div class="flex flex-col gap-4">
    <ReportFilterSelector
      :show-agents-filter="false"
      :show-group-by-filter="false"
      show-inbox-filter
      @filter-change="onFilterChange"
    />

    <!-- Metric Cards -->
    <div class="grid grid-cols-1 gap-4 md:grid-cols-3">
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
        <ReportMetricCard
          :label="$t('CLASSIFICATION_REPORTS.METRICS.TOTAL.LABEL')"
          :value="formatNumber(metrics.total_conversations)"
          :info-text="$t('CLASSIFICATION_REPORTS.METRICS.TOTAL.TOOLTIP')"
        />
      </div>
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
        <ReportMetricCard
          :label="$t('CLASSIFICATION_REPORTS.METRICS.CLASSIFIED.LABEL')"
          :value="
            formatPercentage(
              metrics.classified_count,
              metrics.classified_percentage
            )
          "
          :info-text="$t('CLASSIFICATION_REPORTS.METRICS.CLASSIFIED.TOOLTIP')"
        />
      </div>
      <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
        <ReportMetricCard
          :label="$t('CLASSIFICATION_REPORTS.METRICS.UNCLASSIFIED.LABEL')"
          :value="
            formatPercentage(
              metrics.unclassified_count,
              metrics.unclassified_percentage
            )
          "
          :info-text="$t('CLASSIFICATION_REPORTS.METRICS.UNCLASSIFIED.TOOLTIP')"
        />
      </div>
    </div>

    <!-- Classification Ranking Table -->
    <div class="p-4 border rounded-xl border-n-weak bg-n-solid-2">
      <h3 class="mb-4 text-base font-medium text-n-slate-12">
        {{ $t('CLASSIFICATION_REPORTS.RANKING_TITLE') }}
      </h3>
      <ClassificationRankingTable
        :data="rankings"
        :is-loading="isLoading"
        :current-page="pagination.current_page"
        :total-pages="pagination.total_pages"
        @page-change="onPageChange"
      />
    </div>
  </div>
</template>
